import { useEffect, useMemo, useState } from 'react';
import type { AppConfig } from '../../shared/config';
import type { FeishuFieldMeta } from '../../shared/adapters/feishu-orders';
import './admin.css';

type CountRow = { count: number; outcome?: string; button_id?: string };
type PeriodRow = { period: string; visits: number; lookups: number; actions: number };
type Stats = { total: number; today: number; sevenDays: number; thirtyDays: number; lookups: CountRow[]; buttons: CountRow[]; daily: PeriodRow[]; monthly: PeriodRow[] };

const lookupLabels: Record<string, string> = {
  success: '查询成功',
  not_found: '未找到订单',
  ambiguous: '姓名冲突',
  unavailable: '飞书暂不可用',
  rate_limited: '触发限流'
};

const buttonLabels: Record<string, string> = {
  whatsapp: 'WhatsApp',
  email: 'Email / 复制邮箱',
  tracking: '物流查询',
  copy_email: '复制邮箱'
};

function count(value: unknown) {
  return Number(value) || 0;
}

export function AdminPage({ initial }: { initial: AppConfig }) {
  const [config, setConfig] = useState(initial);
  const [stats, setStats] = useState<Stats>({ total: 0, today: 0, sevenDays: 0, thirtyDays: 0, lookups: [], buttons: [], daily: [], monthly: [] });
  const [versions, setVersions] = useState<Array<{id:number;created_at:number;created_by:string}>>([]);
  const [message, setMessage] = useState('');
  const [feishu, setFeishu] = useState({ app_token: '', table_id: '', mapping: '{\n  "order_number": "Order Number"\n}' });
  const [feishuFields, setFeishuFields] = useState<FeishuFieldMeta[]>([]);
  const [feishuReady, setFeishuReady] = useState(false);

  const refresh = async () => {
    const [configResponse, statsResponse, versionsResponse] = await Promise.all([fetch('/api/admin/config'), fetch('/api/admin/stats'), fetch('/api/admin/config/versions')]);
    if (configResponse.ok) setConfig(await configResponse.json());
    if (statsResponse.ok) setStats(await statsResponse.json());
    if (versionsResponse.ok) setVersions(await versionsResponse.json());
  };

  useEffect(() => {
    refresh().catch(() => setMessage('无法读取后台数据，请检查 Cloudflare Access 和 D1。'));
  }, []);

  const save = async () => {
    const response = await fetch('/api/admin/config', { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify(config) });
    setMessage(response.ok ? '配置已保存并生成新版本。' : '保存失败。');
    if (response.ok) await refresh();
  };

  const previewFeishuFields = async () => {
    const params = new URLSearchParams({ app_token: feishu.app_token, table_id: feishu.table_id });
    const response = await fetch(`/api/admin/feishu/fields?${params}`);
    const data = await response.json() as { fields?: FeishuFieldMeta[]; suggested_mapping?: Record<string, string>; activation_ready?: boolean; errors?: string[] };
    if (!response.ok || !data.fields) {
      setMessage('飞书字段读取失败，请检查 Secrets、App Token、Table ID 和表格权限。');
      setFeishuReady(false);
      return;
    }
    setFeishuFields(data.fields);
    if (data.suggested_mapping && Object.keys(data.suggested_mapping).length) setFeishu({ ...feishu, mapping: JSON.stringify(data.suggested_mapping, null, 2) });
    setFeishuReady(Boolean(data.activation_ready));
    setMessage(data.activation_ready ? '字段预检通过，可以保存映射并启用真实飞书查询。' : `字段预检未通过：${data.errors?.join('；') || '缺少可靠字段'}`);
  };

  const update = (path: 'title'|'description'|'timezone', value: string) => setConfig({ ...config, [path]: value });
  const orderedButtons = useMemo(() => [...config.buttons].sort((a, b) => a.order - b.order), [config.buttons]);
  const lookupTotal = stats.lookups.reduce((sum, row) => sum + count(row.count), 0);
  const buttonTotal = stats.buttons.reduce((sum, row) => sum + count(row.count), 0);
  const latestVersion = versions[0];

  const moveButton = (index:number, direction:-1|1) => {
    const other = orderedButtons[index + direction];
    const current = orderedButtons[index];
    if (!other) return;
    setConfig({
      ...config,
      buttons: config.buttons.map((button) => button.id === current.id ? { ...button, order: other.order } : button.id === other.id ? { ...button, order: current.order } : button)
    });
  };

  return <main className="admin-shell">
    <header className="admin-header">
      <div>
        <p className="eyebrow">TUFTLET</p>
        <h1>售后系统管理后台</h1>
      </div>
      <div className="admin-actions">
        <button type="button" className="secondary-button" onClick={() => refresh().catch(() => setMessage('刷新失败，请稍后再试。'))}>刷新数据</button>
        <a className="admin-link" href="/">查看客户页</a>
      </div>
    </header>

    {message && <p className="admin-message">{message}</p>}

    <section className="stats-grid" aria-label="访问统计">
      <article><span>页面打开总数</span><strong>{count(stats.total)}</strong><small>扫码后成功进入客户页</small></article>
      <article><span>今日访问</span><strong>{count(stats.today)}</strong><small>按后台时区统计</small></article>
      <article><span>近 7 天访问</span><strong>{count(stats.sevenDays)}</strong><small>观察短期投放效果</small></article>
      <article><span>近 30 天访问</span><strong>{count(stats.thirtyDays)}</strong><small>观察长期使用情况</small></article>
    </section>

    <section className="analytics">
      <article className="panel data-panel">
        <div className="panel-title-row"><h2>订单查询结果</h2><span>{lookupTotal} 次</span></div>
        {stats.lookups.length ? <div className="metric-list">{stats.lookups.map((row) => <p key={row.outcome ?? 'unknown'}><span>{lookupLabels[row.outcome ?? ''] ?? row.outcome ?? '未知结果'}</span><strong>{count(row.count)}</strong></p>)}</div> : <p className="empty-state">暂无查询记录</p>}
      </article>
      <article className="panel data-panel">
        <div className="panel-title-row"><h2>客户点击入口</h2><span>{buttonTotal} 次</span></div>
        {stats.buttons.length ? <div className="metric-list">{stats.buttons.map((row) => <p key={row.button_id ?? 'unknown'}><span>{buttonLabels[row.button_id ?? ''] ?? row.button_id ?? '未知入口'}</span><strong>{count(row.count)}</strong></p>)}</div> : <p className="empty-state">暂无点击记录</p>}
      </article>
    </section>

    <section className="stats-tables">
      <article className="panel">
        <div className="panel-title-row"><h2>每日明细</h2><span>最近 31 天</span></div>
        {stats.daily.length ? <div className="table-wrap"><table className="stats-table">
          <thead><tr><th>日期</th><th>访问</th><th>查询</th><th>联系入口</th></tr></thead>
          <tbody>{stats.daily.map((row) => <tr key={row.period}><td>{row.period}</td><td>{count(row.visits)}</td><td>{count(row.lookups)}</td><td>{count(row.actions)}</td></tr>)}</tbody>
        </table></div> : <p className="empty-state">暂无每日数据</p>}
      </article>
      <article className="panel">
        <div className="panel-title-row"><h2>每月汇总</h2><span>最近 12 个月</span></div>
        {stats.monthly.length ? <div className="table-wrap"><table className="stats-table">
          <thead><tr><th>月份</th><th>访问</th><th>查询</th><th>联系入口</th></tr></thead>
          <tbody>{stats.monthly.map((row) => <tr key={row.period}><td>{row.period}</td><td>{count(row.visits)}</td><td>{count(row.lookups)}</td><td>{count(row.actions)}</td></tr>)}</tbody>
        </table></div> : <p className="empty-state">暂无月度数据</p>}
      </article>
    </section>

    <div className="admin-grid">
      <section className="panel">
        <h2>客户页内容</h2>
        <label>页面标题<input value={config.title} onChange={(event)=>update('title', event.target.value)} /></label>
        <label>首页说明<textarea value={config.description} onChange={(event)=>update('description', event.target.value)} /></label>

        <h3>品牌显示</h3>
        <label className="checkbox-label"><input type="checkbox" checked={config.logo.visible} onChange={(event)=>setConfig({...config, logo: {...config.logo, visible: event.target.checked}})} />展示 Logo</label>
        <label>Logo 版本<select value={config.logo.asset} onChange={(event)=>setConfig({...config, logo: {...config.logo, asset: event.target.value as AppConfig['logo']['asset']}})}><option value="transparent">透明版</option><option value="white">白底版</option><option value="original">原版</option></select></label>
        <div className="color-grid">
          <label>品牌主色<input type="color" value={config.theme.primary} onChange={(event)=>setConfig({...config, theme: {...config.theme, primary: event.target.value}})} /></label>
          <label>按钮颜色<input type="color" value={config.theme.action} onChange={(event)=>setConfig({...config, theme: {...config.theme, action: event.target.value}})} /></label>
          <label>点缀颜色<input type="color" value={config.theme.accent} onChange={(event)=>setConfig({...config, theme: {...config.theme, accent: event.target.value}})} /></label>
        </div>

        <h3>联系方式</h3>
        <label>WhatsApp 号码<input value={config.whatsapp.number} onChange={(event)=>setConfig({...config, whatsapp: {...config.whatsapp, number: event.target.value}})} /></label>
        <label>WhatsApp 预填消息<textarea value={config.whatsapp.message} onChange={(event)=>setConfig({...config, whatsapp: {...config.whatsapp, message: event.target.value}})} /></label>
        <label>售后邮箱<input value={config.email.address} onChange={(event)=>setConfig({...config, email: {...config.email, address: event.target.value}})} /></label>
        <label>默认邮件标题<input value={config.email.subject} onChange={(event)=>setConfig({...config, email: {...config.email, subject: event.target.value}})} /></label>

        <h3>提示文案</h3>
        <label>查询失败提示<textarea value={config.messages.notFound} onChange={(event)=>setConfig({...config, messages: {...config.messages, notFound: event.target.value}})} /></label>
        <label>物流暂不可用提示<textarea value={config.messages.unavailable} onChange={(event)=>setConfig({...config, messages: {...config.messages, unavailable: event.target.value}})} /></label>
        <label>后台时区<input value={config.timezone} onChange={(event)=>update('timezone', event.target.value)} /></label>
        <button className="admin-save" onClick={save}>保存并发布</button>
      </section>

      <section className="panel">
        <h2>按钮入口</h2>
        {orderedButtons.map((button,index)=><div className="button-row" key={button.id}>
          <div><input aria-label={`${button.id} label`} value={button.label} onChange={(event)=>setConfig({...config, buttons: config.buttons.map((candidate)=>candidate.id===button.id ? {...candidate, label: event.target.value} : candidate)})}/>{button.type==='custom'&&<input aria-label="HTTPS URL" value={button.url} onChange={(event)=>setConfig({...config, buttons: config.buttons.map((candidate)=>candidate.id===button.id&&candidate.type==='custom' ? {...candidate, url: event.target.value} : candidate)})}/>}</div>
          <label className="row-toggle"><input type="checkbox" checked={button.enabled} onChange={(event)=>setConfig({...config, buttons: config.buttons.map((candidate)=>candidate.id===button.id ? {...candidate, enabled: event.target.checked} : candidate)})}/>启用</label>
          <div className="row-actions"><button disabled={index===0} onClick={()=>moveButton(index,-1)}>↑</button><button disabled={index===orderedButtons.length-1} onClick={()=>moveButton(index,1)}>↓</button></div>
        </div>)}
        <button className="secondary-button wide" onClick={()=>setConfig({...config, buttons: [...config.buttons, {id: `link-${crypto.randomUUID().slice(0,8)}`, type: 'custom', label: 'New support link', url: 'https://example.com', enabled: false, order: Math.max(0, ...config.buttons.map((button)=>button.order)) + 1}]})}>添加 HTTPS 链接</button>

        <h2 className="section-heading">飞书查询</h2>
        <label>App Token<input value={feishu.app_token} onChange={(event)=>setFeishu({...feishu, app_token: event.target.value})}/></label>
        <label>Table ID<input value={feishu.table_id} onChange={(event)=>setFeishu({...feishu, table_id: event.target.value})}/></label>
        <button className="secondary-button wide" onClick={previewFeishuFields}>读取字段元数据</button>
        {feishuFields.length > 0 && <div className="field-preview"><p>字段预检：{feishuReady ? '通过' : '未通过'}</p>{feishuFields.map((field)=><p key={field.field_name}>{field.field_name} · type {String(field.type ?? 'unknown')}</p>)}</div>}
        <label>标准字段到飞书字段 JSON<textarea value={feishu.mapping} onChange={(event)=>setFeishu({...feishu, mapping: event.target.value})}/></label>
        <button className="secondary-button wide" onClick={async()=>{try{const response=await fetch('/api/admin/feishu/mapping',{method:'PUT',headers:{'content-type':'application/json'},body:JSON.stringify({app_token:feishu.app_token,table_id:feishu.table_id,fields:feishuFields,mapping:JSON.parse(feishu.mapping)})});setMessage(response.ok?'飞书字段映射已保存，真实飞书查询已启用。':'飞书字段映射保存失败，请先读取字段元数据并确认 order_created_at。')}catch{setMessage('字段映射 JSON 格式错误。')}}}>保存字段映射</button>

        <h2 className="section-heading">版本历史</h2>
        {latestVersion && <p className="latest-version">当前最近版本：#{latestVersion.id} · {new Date(latestVersion.created_at*1000).toLocaleString()}</p>}
        {versions.length ? versions.map((version)=><div className="version" key={version.id}><span>#{version.id} · {new Date(version.created_at*1000).toLocaleString()}</span><button onClick={async()=>{await fetch(`/api/admin/config/versions/${version.id}/restore`,{method:'POST'});await refresh();}}>恢复</button></div>) : <p className="empty-state">尚无 D1 版本记录</p>}
      </section>
    </div>
  </main>;
}
